import { gql } from "@apollo/client/core";
import { Asset, AssetType, FileInfo, MaterialResource } from "../types";
import { ParseError, UnhandledBehaviorError } from "../util/exception";
import { persistenceService } from "./persistence";
import { graphQlManager } from "./server";
import { getAttributesFromArrayBuffer } from "../util/image";
import { sanitizeSVGDimensions } from "../util/illustration";
import { xmlSerializer } from "../util/crossplatform";

interface AssetCreation {
    asset: Asset;
    uploadUrl: string;
}

interface AssetCreationInfo {
    assetResponse: AssetCreation;
    mimeType: string;
}

const assetMetadataFragment = gql`
    fragment AssetMetadataFields on Asset {
        metadata {
            key
            value
        }
    }
`;

export const assetFragment = (includeMetadata: boolean) => {
    return gql`
        ${(includeMetadata && assetMetadataFragment) || ""}
        fragment AssetFields on Asset {
            name
            key
            type
            createdAt
            mimeType
            fileLink
            assetConfiguration {
                assetKey
                colorOption {
                    id
                    name
                    variants {
                        id
                        name
                        enabled
                        namedColor
                        color
                    }
                }
                channelNumbers {
                    id
                    number
                }
                defaultColorVariants {
                    channelNumber
                    variant {
                        id
                        name
                        enabled
                        namedColor
                        color
                    }
                }
            }
            versions {
                name
                link
            }
            ${(includeMetadata && "...AssetMetadataFields") || ""}
        }
    `;
};

const getAssetsQuery = gql`
    ${assetFragment(false)}
    query GetAssets($keys: [String]!) {
        assets(keys: $keys) {
            ...AssetFields
        }
    }
`;

export const materialFragment = gql`
    fragment MaterialFields on Material {
        id
        name
        createdAt
        clearCoat
        clearCoatIor
        refractionIntensity
        reflectionIntensity
        reflectionRotation
        albedoMap {
            key
            fileLink
        }
        alphaMap {
            key
            fileLink
        }
        ambientMap {
            key
            fileLink
        }
        emissionMap {
            key
            fileLink
        }
        metallicMap {
            key
            fileLink
        }
        normalMap {
            key
            fileLink
        }
        refractionMap {
            key
            fileLink
        }
        reflectionMap {
            key
            fileLink
        }
        roughnessMap {
            key
            fileLink
        }
    }
`;

const getMaterialsQuery = gql`
    ${materialFragment}
    query GetMaterials($ids: [String]) {
        materials(id: $ids) {
            ...MaterialFields
        }
    }
`;

const createAssetMutation = gql`
    mutation CreateAsset($name: String!, $type: String!, $mimeType: String!, $anonymous: Boolean, $temporary: Boolean) {
        assetCreate(name: $name, type: $type, mimeType: $mimeType, anonymous: $anonymous, temporary: $temporary) {
            uploadUrl
            asset {
                name
                key
                type
                createdAt
                mimeType
                fileLink
            }
        }
    }
`;

const removeBackgroundFromAssetMutation = gql`
    ${assetFragment(false)}
    mutation RemoveBackgroundFromAsset($key: String!) {
        assetRemoveBackground(key: $key) {
            ...AssetFields
        }
    }
`;

export const getAssets = async (assetKeys: string[]): Promise<Asset[]> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{ assets: Asset[] }>({
        query: getAssetsQuery,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            keys: assetKeys,
        },
    });
    return response.data.assets;
};

const getMaterials = async (ids: string[]): Promise<MaterialResource[]> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{ materials: MaterialResource[] }>({
        query: getMaterialsQuery,
        errorPolicy: "all",
        variables: {
            ids,
        },
    });
    return response.data.materials;
};

const createAsset = async (
    name: string,
    type: string,
    mimeType: string,
    anonymous?: boolean,
    temporary?: boolean,
): Promise<undefined | AssetCreation> => {
    const response = await graphQlManager.getShadowGraphqlClient().mutate<{ assetCreate: AssetCreation }>({
        mutation: createAssetMutation,
        errorPolicy: "all",
        fetchPolicy: "no-cache",
        variables: {
            name,
            type,
            mimeType,
            anonymous,
            temporary,
        },
    });
    return response.data?.assetCreate;
};

/**
 * An asset manager provides a way to create and
 * manage assets on the Spiff Commerce Platform.
 */
export interface AssetManager {
    /**
     * Uploads a file to the Spiff Commerce Platform.
     */
    uploadFile: (file: File, onProgress: (val: number) => void) => Promise<Asset>;

    /**
     * From an existing asset, generates a new asset that has the background replaced with transparency.
     * This process is idempotent, i.e. it will only run once for a given asset.
     * @param asset The existing asset to remove the background from.
     * @returns A promise that resolves with a new asset.
     */
    removeBackgroundFromAsset(asset: Asset): Promise<Asset>;
}

class AssetService implements AssetManager {
    private cache = new Map<string, Promise<Asset>>();
    /**
     * Promise cache for BG removal processes. Values only present while process is active.
     * Use BGRMStorage and the regular asset promise cache to cache the actual objects.
     */
    private bgrmProcessCache = new Map<string, Promise<Asset>>();
    private materialCache = new Map<string, Promise<MaterialResource>>();

    /**
     * Allows for retrieving an asset, returns the option from a cache if possible.
     */
    async getLocalOrFromServer(assetKey: string): Promise<Asset> {
        if (this.cache.has(assetKey)) {
            const asset = this.cache.get(assetKey);
            if (!asset) throw new UnhandledBehaviorError("Failed to get asset from cache!");
            return asset;
        }
        const assetResolver = async () => {
            const asset = (await getAssets([assetKey]))[0];
            return asset;
        };
        const resolver = assetResolver();
        this.cache.set(assetKey, resolver);
        return resolver;
    }

    /**
     * Retrieves the asset from the server, bypassing cache (but still writing the result to cache)
     */
    async getFromServer(assetKey: string): Promise<Asset> {
        const assetResolver = async () => {
            const asset = (await getAssets([assetKey]))[0];
            return asset;
        };
        const resolver = assetResolver();
        this.cache.set(assetKey, resolver);
        return resolver;
    }

    keyFromURL(url: string): string | undefined {
        let key: string | undefined = undefined;
        try {
            key = new URL(url).pathname;
        } catch (e) {
            key = undefined;
        }
        if (key !== undefined && key.startsWith("/")) {
            key = key.replace("/", "");
        }
        return key?.split("?")[0];
    }

    /**
     * Caches an asset if it doesn't already exist.
     */
    cacheAsset(asset: Asset) {
        if (!asset.key) throw new UnhandledBehaviorError("Asset has no key!");
        if (this.cache.has(asset.key)) {
            return;
        }
        this.cache.set(asset.key, Promise.resolve(asset));
    }

    /**
     * Caches a material if it doesn't already exist.
     */
    cacheMaterial(material: MaterialResource) {
        if (!material.id) throw new UnhandledBehaviorError("Material has no id!");
        if (this.materialCache.has(material.id)) {
            return;
        }
        this.materialCache.set(material.id, Promise.resolve(material));
    }

    /**
     * Allows for retrieving a material, returns the option from a cache if possible.
     * @param id The option ID to be retrieved.
     */
    async getMaterialLocalOrFromServer(id: string): Promise<MaterialResource> {
        if (this.materialCache.has(id)) {
            return this.materialCache.get(id)!;
        }
        const materialResolver = async () => {
            const material = (await getMaterials([id]))[0];
            return material;
        };
        const resolver = materialResolver();
        this.materialCache.set(id, resolver);
        return resolver;
    }

    /**
     * Upload a user asset to the server. Using callbacks to notify important events.
     * The asset will be stored via the persistence service for future access, if available.
     */
    async uploadAssetWithProgress(
        file: FileInfo,
        assetType: AssetType,
        onProgress: (val: number) => void,
        anonymous?: boolean,
        temporary?: boolean,
    ): Promise<Asset> {
        // We request the server to create a new asset entity.
        const creationResponse = await this.dispatchCreateAssetRequest(file, assetType, anonymous, temporary);
        if (!creationResponse) {
            throw new Error("Failed to create asset.");
        }

        // Organise a request & set up header and associated authentication. We use the provided
        // presigned URL to upload the users asset data to the server. The asset isn't valid until this is done.
        await new Promise<Asset>((resolve, reject) => {
            const req = new XMLHttpRequest();
            req.open("PUT", creationResponse.assetResponse.uploadUrl, true);
            req.setRequestHeader("Content-Type", creationResponse.mimeType);
            req.setRequestHeader("Cache-Control", "public,max-age=31536000,immutable");
            req.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    onProgress((event.loaded * 100) / event.total);
                }
            };
            req.onload = () => {
                const responseData = creationResponse.assetResponse.asset;
                AssetStorage.add(creationResponse.assetResponse.asset);
                resolve(responseData);
            };
            req.onerror = reject;
            this.postProcessFileUpload(file, creationResponse.mimeType)
                .then((processedInfo) => req.send(processedInfo.blob))
                .catch(reject);
        });
        this.cacheAsset(creationResponse.assetResponse.asset);
        return creationResponse.assetResponse.asset;
    }

    async uploadAsset(file: FileInfo, assetType: AssetType, anonymous?: boolean, temporary?: boolean): Promise<Asset> {
        return this.uploadAssetWithProgress(file, assetType, () => {}, anonymous, temporary);
    }

    async uploadFile(file: File, onProgress: (val: number) => void): Promise<Asset> {
        const isRaster = this.isRaster(file);
        const assetType = !isRaster ? AssetType.Illustration : AssetType.Image;
        const fileInfo = await this.loadImageAsFileInfo(file);
        return await this.uploadAssetWithProgress(fileInfo, assetType, onProgress, true);
    }

    async removeBackgroundFromAsset(asset: Asset): Promise<Asset> {
        const originalKey = asset.key!;
        if (this.bgrmProcessCache.has(originalKey)) {
            return this.bgrmProcessCache.get(originalKey)!;
        }
        if (BGRMStorage.has(originalKey)) {
            const promise = this.getLocalOrFromServer(BGRMStorage.get(originalKey)!);
            this.bgrmProcessCache.set(originalKey, promise);
            return promise;
        }
        const processFunc = async (): Promise<Asset> => {
            const removeResponse = await graphQlManager
                .getShadowGraphqlClient()
                .mutate<{ assetRemoveBackground: Asset }>({
                    mutation: removeBackgroundFromAssetMutation,
                    fetchPolicy: "no-cache",
                    errorPolicy: "all",
                    variables: {
                        key: originalKey,
                    },
                });
            this.bgrmProcessCache.delete(originalKey);
            if (!removeResponse.data?.assetRemoveBackground?.key) {
                throw new Error("Failed to remove background from image");
            }
            AssetStorage.add(removeResponse.data.assetRemoveBackground);
            BGRMStorage.add(originalKey, removeResponse.data.assetRemoveBackground.key!);
            this.cache.set(
                removeResponse.data.assetRemoveBackground.key!,
                Promise.resolve(removeResponse.data.assetRemoveBackground),
            );
            return removeResponse.data.assetRemoveBackground;
        };
        const promise = processFunc();
        this.bgrmProcessCache.set(originalKey, promise);
        return promise;
    }

    removePersistedAsset(assetKey: string) {
        AssetStorage.remove(assetKey);
        BGRMStorage.delete(assetKey);
        BGRMStorage.deleteForBgRemovedKey(assetKey);
    }

    getPersistedAssets(): PersistedAsset[] {
        return AssetStorage.list();
    }

    registerPersistedAssetListener(callback: () => void) {
        AssetStorage.addCallback(callback);
    }

    unRegisterPersistedAssetListener(callback: () => void) {
        AssetStorage.removeCallback(callback);
    }

    /**
     * Convert a File object for an image into a FileInfo.
     */
    loadImageAsFileInfo = async (file: File): Promise<FileInfo> => {
        // Rasters are slightly more involved.
        const arrayBuffer = await file.arrayBuffer();
        const attributes = await getAttributesFromArrayBuffer(arrayBuffer);
        const dataUrlToBlob = (dataUrl: string, type: string) => {
            const binary = atob(dataUrl.split(",")[1]);
            const array: number[] = [];
            for (let i = 0; i < binary.length; i++) {
                array.push(binary.charCodeAt(i));
            }
            return new Blob([new Uint8Array(array)], { type });
        };
        return {
            name: file.name.substring(file.name.lastIndexOf("/") + 1),
            blob: dataUrlToBlob(attributes.dataUrl, file.type),
        };
    };

    private isRaster(file: File): boolean {
        return !(
            file.type === "image/svg+xml" ||
            file.type === "application/pdf" ||
            file.type === "application/postscript"
        );
    }

    private async postProcessFileUpload(file: FileInfo, type: string) {
        if (type === "image/svg+xml") {
            const text = await file.blob.text();
            const parser = new window.DOMParser();
            const svgDom = parser.parseFromString(text, "image/svg+xml");
            const root = svgDom.documentElement;
            if (!root) throw new ParseError("Failed to read SVG.");
            sanitizeSVGDimensions(root);
            const serialiser = xmlSerializer();
            const rebuiltSvg = serialiser.serializeToString(root);
            return {
                name: file.name,
                blob: new Blob([rebuiltSvg], { type: "image/svg+xml" }),
            };
        }
        return file;
    }

    /**
     * Handles mimeType resolution & asset creation request
     * @param file A file info object containing data about the file and its name
     * @param assetType The type of asset we're expecting to upload
     */
    private async dispatchCreateAssetRequest(
        file: FileInfo,
        assetType: AssetType,
        anonymous?: boolean,
        temporary?: boolean,
    ): Promise<undefined | AssetCreationInfo> {
        // Send a request for asset creation to server
        // We get a presigned upload URL back
        const mimeType = file.blob.type ? file.blob.type : this.guessMIME(file.name);
        const assetResponse = await createAsset(file.name, assetType, mimeType, anonymous, temporary);
        if (!assetResponse) {
            return undefined;
        }

        return {
            assetResponse,
            mimeType,
        };
    }

    private guessMIME(ext: string): string {
        const extension = ext.split(".").pop();
        switch (extension) {
            case "glb":
                return "model/gltf-binary";
            case "ttf":
                return "font/ttf";
            case "mkv":
                return "video/x-matroska";
            default:
                throw new UnhandledBehaviorError("Unexpected mimetype: " + extension);
        }
    }
}

interface PersistedAsset {
    assetKey: string;
    src: string;
}

const ASSET_PERSISTENCE_KEY = "persistentAssets";
/**
 * Some bundled functionality for dealing with assets in persistence.
 */
class AssetStorage {
    private static callbacks: (() => void)[] = [];

    /**
     * Add a new asset to persistence
     * @param asset The asset to add.
     */
    static add(asset: Asset) {
        if (!asset.fileLink) {
            console.error("Failed to find cdn link on asset, cannot persist!");
            return;
        }
        // Create a new map to store data
        const newPersistentAssetsMap = new Map<string, string>();
        newPersistentAssetsMap.set(asset.key || "", asset.fileLink);
        // Merge existing keys if available, into the new map.
        const oldPersistentAssets = persistenceService.getMap(ASSET_PERSISTENCE_KEY);
        if (oldPersistentAssets) {
            oldPersistentAssets.forEach((value, key) => {
                newPersistentAssetsMap.set(key, value);
            });
        }
        // Save the new map with all keys.
        persistenceService.setMap(ASSET_PERSISTENCE_KEY, newPersistentAssetsMap);
        AssetStorage.executeCallbacks();
    }
    /**
     * Remove an existing asset from persistence. Noop if the asset doesn't exist.
     * @param assetKey The key to remove.
     */
    static remove(assetKey: string) {
        const uploadedAssetsMap = persistenceService.getMap(ASSET_PERSISTENCE_KEY);
        if (!uploadedAssetsMap) return;
        const entryToRemove = Array.from(uploadedAssetsMap.entries()).find((m) => m[0] === assetKey);
        if (!entryToRemove) return;
        uploadedAssetsMap.delete(entryToRemove[0]);
        persistenceService.setMap(ASSET_PERSISTENCE_KEY, uploadedAssetsMap);
        AssetStorage.executeCallbacks();
    }
    /**
     * Get a list of all persisted assets.
     */
    static list(): PersistedAsset[] {
        const uploadedAssetsMap = persistenceService.getMap(ASSET_PERSISTENCE_KEY);
        if (!uploadedAssetsMap) return [];
        return Array.from(uploadedAssetsMap.entries()).map((m) => ({ assetKey: m[0], src: m[1] }));
    }
    /**
     * Adds a callback that will be called whenever assets are added/removed from persistence.
     */
    static addCallback(callback: () => void) {
        AssetStorage.callbacks.push(callback);
    }
    /**
     * Removes an existing callback.
     */
    static removeCallback(callback: () => void) {
        AssetStorage.callbacks = AssetStorage.callbacks.filter((cb) => cb !== callback);
    }

    private static executeCallbacks() {
        AssetStorage.callbacks.forEach((cb) => cb());
    }
}

const BGRM_ASSET_PERSISTENCE_KEY = "bgrmPersistentAssets";
class BGRMStorage {
    static has(originalKey: string): boolean {
        return BGRMStorage.getMap().has(originalKey);
    }
    static get(originalKey: string): string | undefined {
        return BGRMStorage.getMap().get(originalKey);
    }
    static keys(): IterableIterator<string> {
        return BGRMStorage.getMap().keys();
    }
    static values(): IterableIterator<string> {
        return BGRMStorage.getMap().values();
    }
    static add(originalKey: string, bgRemovedKey: string) {
        const cache = BGRMStorage.getMap();
        cache.set(originalKey, bgRemovedKey);
        BGRMStorage.setMap(cache);
    }
    static delete(originalKey: string) {
        const cache = BGRMStorage.getMap();
        cache.delete(originalKey);
        BGRMStorage.setMap(cache);
    }
    static deleteForBgRemovedKey(bgRemovedKey: string) {
        const cache = BGRMStorage.getMap();
        const originalKey = Array.from(cache.keys()).find((key) => cache.get(key) === bgRemovedKey);
        if (originalKey) {
            cache.delete(originalKey);
            BGRMStorage.setMap(cache);
        }
    }

    private static getMap(): Map<string, string> {
        return persistenceService.getMap(BGRM_ASSET_PERSISTENCE_KEY) || new Map();
    }
    private static setMap(map: Map<string, string>) {
        persistenceService.setMap(BGRM_ASSET_PERSISTENCE_KEY, map);
    }
}

export const assetService = new AssetService();
